/**
 * CustomSchoolsReport.jsx — Aug 2026
 * Admin view of school/college names students typed via the "Other" box that
 * aren't in the official list, so admins can promote popular ones. Read-only.
 */
import React, { useEffect, useState } from 'react';
import { apiGet } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { School, Loader2, Search, Users } from 'lucide-react';

export default function CustomSchoolsReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    apiGet('/admin/schools/custom')
      .then((d) => setRows(d.custom_schools || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="custom-schools-report">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600">
          <School className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold text-slate-800">New Schools (from "Other")</h2>
          <p className="text-sm text-slate-500">Names students typed themselves — promote popular ones into the official dropdown.</p>
        </div>
      </div>

      <Card className="p-5 rounded-2xl border-0 ring-1 ring-slate-100">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-slate-500">{rows.length} unique custom entries</span>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="custom-school-search" placeholder="Search"
              value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9 w-56 rounded-xl" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="custom-schools-empty">No custom school names yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                  <th className="py-2 pr-3">School / College</th>
                  <th className="py-2 pr-3 text-center">Students</th>
                  <th className="py-2 pr-3">Cities</th>
                  <th className="py-2 pr-3">Boards</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`custom-school-row-${i}`}>
                    <td className="py-2.5 pr-3 font-medium text-slate-700">{r.name}</td>
                    <td className="py-2.5 pr-3 text-center">
                      <span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" />{r.count}</span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-500">{(r.cities || []).join(', ') || '—'}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{(r.boards || []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
